import { FieldTypes } from '../constants/FieldTypes'
import {
  BooleanField,
  DropdownField,
  FieldMap,
  ListField,
  NumberField,
  TextField,
} from '../types/forms/fields'
import {
  deliveryDateField,
  deliveryLocationField,
  deliveryLocationOtherField,
  deliveryTypesField,
  newbornFnameField,
  newbornLnameField,
  newbornSexField,
  newbornWeightField,
} from './delivery'
import { patientLmpField } from './introduction'
import { getBooleanHistoryField, getPleaseSpecifyField } from './shared'
import { addFieldRow, createTextAreaField } from './utils'

export const brithLocationField: DropdownField = {
  placeholder: 'Birth Location',
  type: FieldTypes.DROPDOWN,
  options: [
    { id: 'home', text: 'Home' },
    { id: 'hospital', text: 'Hospital' },
    { id: 'birthing-center', text: 'Birthing center' },
  ],
}

export const previousPregnancyBirthLocationField: DropdownField = {
  ...brithLocationField,
  allowOther: true,
  condition: v =>
    v?.pregnancyOutcome === 'born-living' || v?.pregnancyOutcome === 'born-with-complications',
}

export const signOnBirthLocationField: DropdownField = {
  ...brithLocationField,
  labels: [{ text: 'What type of birth are you planning/leaning towards?' }],
}

export const pregnancyOutcomeField: DropdownField = {
  placeholder: 'Pregnancy Outcome',
  type: FieldTypes.DROPDOWN,
  options: [
    { id: 'born-living', text: 'Born Living' },
    { id: 'born-with-complications', text: 'Born with Complications' },
    { id: 'miscarriage', text: 'Miscarriage' },
    // { id: 'stillborn', text: 'Stillborn' },
    { id: 'abortion', text: 'Abortion' },
    { id: 'other', text: 'Other' },
  ],
}

export const pregnancyOutcomeOtherField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Other pregnancy outcome',
  condition: v => v?.pregnancyOutcome === 'other',
}
export const deliveryTypeField: DropdownField = {
  placeholder: 'Delivery Type',
  type: FieldTypes.DROPDOWN,
  options: [
    { id: 'vaginal', text: 'Vaginal' },
    { id: 'cesarean', text: 'Cesarean' },
  ],
  condition: v =>
    v?.pregnancyOutcome === 'born-living' || v?.pregnancyOutcome === 'born-with-complications',
}

export const weeksGestationField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Weeks Gestation',
  min: 0,
  max: 99,
}

export const nameOfChildField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Name of Child',
  condition: v =>
    v?.pregnancyOutcome === 'born-living' || v?.pregnancyOutcome === 'born-with-complications',
  optional: true,
}

export const problemsDuringPregnancyField = getBooleanHistoryField(
  'Problems or illnesses during pregnancy',
)

export const treatmentsDuringPregnancyField = getBooleanHistoryField(
  'Medications, treatments, or interventions during pregnancy',
)

export const drugOrAlcoholUseDuringPregnancyField = getBooleanHistoryField(
  'Drug or alcohol use during pregnancy',
)

export const complicationsDuringBirthField = getBooleanHistoryField(
  "Complications during child's birth",
)

export const complicationsDuringInfancyField = getBooleanHistoryField(
  "Complications during this child's infancy",
)

export const childsGestationAndBirthField: FieldMap = {
  name: "Child's Gestation and Birth",
  condition: v =>
    v?.pregnancyOutcome === 'born-living' || v?.pregnancyOutcome === 'born-with-complications',
  children: {
    problemsDuringPregnancy: problemsDuringPregnancyField,
    problemsDuringPregnancyDetails: getPleaseSpecifyField('problemsDuringPregnancy'),
    treatmentsDuringPregnancy: treatmentsDuringPregnancyField,
    treatmentsDuringPregnancyDetails: getPleaseSpecifyField('treatmentsDuringPregnancy'),
    drugOrAlcoholUseDuringPregnancy: drugOrAlcoholUseDuringPregnancyField,
    drugOrAlcoholUseDuringPregnancyDetails: getPleaseSpecifyField(
      'drugOrAlcoholUseDuringPregnancy',
    ),
    complicationsDuringBirth: complicationsDuringBirthField,
    complicationsDuringBirthDetails: getPleaseSpecifyField('complicationsDuringBirth'),
    complicationsDuringInfancy: complicationsDuringInfancyField,
    complicationsDuringInfancyDetails: getPleaseSpecifyField('complicationsDuringInfancy'),
  },
}

export const childExposuresAndLifestyleField: FieldMap = {
  name: 'Child Exposures and Lifestyle',
  condition: v =>
    v?.pregnancyOutcome === 'born-living' || v?.pregnancyOutcome === 'born-with-complications',
  children: {
    livingWith: createTextAreaField(
      'Who does the child live with? Please list all adults and children',
    ),
    careParticipants: createTextAreaField('Who participates in caring for the child?'),
    movingHistory: createTextAreaField('How many times has the child moved in the past year?'),
    schoolLocation: createTextAreaField(
      "What is the name and city of the child's school or daycare?",
    ),
    specialServicesEvauluations: createTextAreaField(
      'Has your child been evauluated for special services? If so, what were the results?',
    ),
    readingSchedule: createTextAreaField('How often do you read to your child?'),
    familyEatingSchdule: createTextAreaField('How often does your family eat together?'),
    childSafetyAfterVisit: createTextAreaField('Will your child be safe when going home today?'),
    firearmExposure: createTextAreaField(
      'Will your child be exposed to any firearms that are not locked up?',
    ),
    neighborhoodSafety: createTextAreaField('Is your neighborhood safe?'),
  },
}

export const previousPregnancyField: FieldMap = {
  name: 'Previous Pregnancy',
  children: {
    pregnancyOutcome: pregnancyOutcomeField,
    pregnancyOutcomeOther: pregnancyOutcomeOtherField,
    deliveryTypes: deliveryTypesField,
    deliveryLocation: deliveryLocationField,
    deliveryLocationOther: deliveryLocationOtherField,
    newbornFname: addFieldRow(newbornFnameField, { index: 0, flex: 1 }),
    newbornLname: addFieldRow(newbornLnameField, { index: 0, flex: 1 }),
    lmp: patientLmpField,
    deliveredOn: addFieldRow(deliveryDateField, { index: 1, flex: 1 }),
    newbornSex: addFieldRow(newbornSexField, { index: 1, flex: 1 }),
    newbornWeight: newbornWeightField,
    childsGestationAndBirth: childsGestationAndBirthField,
  },
}

export const hasPreviousPregnancyField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  placeholder: 'Have you had a previous pregnancy?',
}

export const previousPregnanciesField: ListField = {
  name: 'Previous Pregnancies',
  itemFields: previousPregnancyField,
  itemName: 'previous pregnancy',
  labels: [
    {
      text: 'Please indicate below any prior pregnancies, regardless of outcomes (living, stillborn, miscarriage, abortion, etc.)',
    },
  ],
}
